import { ComponentProps, forwardRef, PropsWithChildren, ReactNode, useState } from 'react';
import { BackupTable, CircleNotifications, ExpandLess, ExpandMore, LibraryBooks } from '@mui/icons-material';
import {
  Collapse,
  IconButton,
  List,
  ListItemBaseProps,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { NavLink, NavLinkProps } from 'react-router-dom';

export const MuiNavLink = forwardRef<HTMLAnchorElement, NavLinkProps>((navLinkProps, ref) => {
  const { className: previousClasses, ...rest } = navLinkProps;
  const elementClasses = previousClasses?.toString() ?? '';
  return (
    <NavLink
      {...rest}
      ref={ref}
      className={({ isActive }) => (isActive ? elementClasses + ' Mui-selected' : elementClasses)}
    />
  );
});
MuiNavLink.displayName = 'MuiNavLink';

type ListItemNavLinkProps = PropsWithChildren<{
  to: string;
  text: ComponentProps<typeof ListItemText>['primary'];
  icon: ReactNode;
  onClick?: () => void;
  end?: boolean;
  isNested?: boolean;
}> &
  ListItemBaseProps;
export const ListItemNavLink = ({ to, end, icon, text, onClick, isNested, sx, ...rest }: ListItemNavLinkProps) => {
  return (
    <ListItemButton
      component={MuiNavLink}
      dense={isNested}
      to={to}
      onClick={onClick}
      end={end}
      sx={{
        ...sx,
        ...(isNested ? { pl: 4 } : {}),
      }}
      {...rest}
    >
      <ListItemIcon
        sx={{
          '.Mui-selected > &': { color: (theme) => theme.palette.primary.main },
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItemButton>
  );
};

type CollapsibleNavLinkProps = ListItemNavLinkProps & {
  children: ReactNode;
  defaultOpen?: boolean;
};

export const CollapsibleListItemNavLink = ({
  children,
  defaultOpen = false,
  to,
  text,
  icon,
}: CollapsibleNavLinkProps) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <ListItemButton component={MuiNavLink} to={to}>
        <ListItemIcon
          sx={{
            '.Mui-selected > &': {
              color: (theme) => theme.palette.primary.main,
            },
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText primary={text} />
        <IconButton onClick={() => setOpen((prev) => !prev)}>
          {open ? <ExpandLess color={'action'} /> : <ExpandMore color={'action'} />}
        </IconButton>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </>
  );
};
